/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { LuisText } from 'src/core/components/LuisText';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { isEntityOfStoreType } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

export type EntityFeaturesCellProps = {
    entity: EntityStore;

    readonly allEntities: EntityStore[];
};

const { styleComponent } = getStylistV2('EntityFeaturesCell');

const Wrapper = styleComponent<IStackProps>(Stack)('Wrapper', {
    flexWrap: 'wrap',
    lineHeight: '24px',
    whiteSpace: 'pre-wrap'
});

const theme = getScopedTheme('EntityFeaturesCell');

/**
 * Renders a container to display features
 * used by a certain entity.
 */
export const EntityFeaturesCell = observer((props: EntityFeaturesCellProps) => {
    const { entity, allEntities } = props;
    let output: React.ReactNode;

    if (isEntityOfStoreType<SimpleEntityStore>(entity, ModelType.Simple)) {
        output = entity.featureStoreHouse.items
            .map(feature => allEntities.find(entityStore => entityStore.id === feature.id)?.name)
            .filter(name => name)
            .join(', ');
    }

    if (!output) {
        output = <Icon iconName="Remove" />;
    }

    return (
        <Wrapper horizontal verticalAlign="start">
            <LuisText color={theme.fontColor}>{output}</LuisText>
        </Wrapper>
    );
});
